import {
  Banner,
  Typography,
  useMediaQuery,
  useTheme,
} from '@forgeglobal/design-framework';
import { ValuationModel } from '@tcs/client-portal-api/platform_generated/models/ValuationModel';
import { AssetValuationDetails } from '@tcs/client-portal-api/types/AssetValuationDetails';
import { Persona } from '@tcs/client-portal-api/types/Persona';
import ValuationAssetDetail from '@tcs/client-portal-common/components/organisms/ValuationAssetDetail';
import useDialogOpen from '@tcs/client-portal-common/hooks/useDialogOpen';
import useSession from '@tcs/client-portal-common/hooks/useSession';
import { getConfig } from '@tcs/client-portal/config/configService';
import StatusButton from '@tcs/common/components/atoms/StatusButton';
import DetailCard, {
  DetailCardItem,
} from '@tcs/common/components/molecules/DetailCard';
import formatCurrency from '@tcs/common/utils/formatCurrency';
import formatDate from '@tcs/common/utils/formatDate';
import { Unicode } from '@tcs/common/utils/unicode';
import addDays from 'date-fns/addDays';
import differenceInDays from 'date-fns/differenceInDays';
import { useStyles } from './style';

interface ValuationDetailsCardProps {
  accountNumber: string;
  asset: AssetValuationDetails;
}

const DAYS_TILL_WARNING = 60;
const ONE_YEAR = 365;

const ValuationDetailsCard = ({
  accountNumber,
  asset,
}: ValuationDetailsCardProps) => {
  const classes = useStyles();
  const {
    id,
    name,
    assetType,
    currentValue,
    valuationDate,
    status,
    isUpdateable,
    zendeskTicketId,
    submissionDate,
  } = asset;

  const dialogName = `valuation-accountNumber-${accountNumber}-asset-${id}`;
  const { setDialogOpen } = useDialogOpen(dialogName);

  const theme = useTheme();
  const aboveMdBreakpoint = useMediaQuery(theme.breakpoints.up('md'));

  const valuationDueDate = formatDate(
    addDays(new Date(valuationDate || '1970-01-01'), ONE_YEAR).toDateString()
  );

  const getValuationStatus = () => {
    if (status === ValuationModel.ValuationStatus.NEW) {
      return 'info';
    }

    const daysToDueDate =
      differenceInDays(new Date(valuationDueDate), new Date()) + 1;

    let valuationStatus: 'info' | 'error' | 'warning' = 'info';

    if (daysToDueDate <= 0) {
      valuationStatus = 'error';
    } else if (daysToDueDate <= DAYS_TILL_WARNING) {
      valuationStatus = 'warning';
    }
    return valuationStatus;
  };

  const valuationStatus = getValuationStatus();

  const {
    claims: { clientPortalPersona: persona },
  } = useSession();

  const config = getConfig();

  const getBanner = () => {
    if (!isUpdateable) {
      return null;
    }

    const getZendeskLink = () => {
      if (persona !== Persona.Admin) {
        return undefined;
      }
      if (!zendeskTicketId) {
        return (
          <p className={classes.adminMessage}>
            No Zendesk ticket was created for this valuation.
          </p>
        );
      }

      return (
        <p className={classes.adminMessage}>
          <a
            href={`${config.ZEN_DESK_BASE_URL}/agent/tickets/${zendeskTicketId}`}
            target="_blank"
            rel="noreferrer">
            View on Zendesk
          </a>
        </p>
      );
    };

    switch (valuationStatus) {
      case 'error':
        return (
          <div className={classes.bannerWrapper}>
            <Banner severity="error" bannerType="inline">
              This asset valuation is past due.
              {getZendeskLink()}
            </Banner>
          </div>
        );

      case 'warning':
        return (
          <div className={classes.bannerWrapper}>
            <Banner severity="warning" bannerType="inline">
              You need to update the value by {valuationDueDate}.
              {getZendeskLink()}
            </Banner>
          </div>
        );
      default:
        return null;
    }
  };

  const getStatusButtonText = () => {
    if (valuationStatus === 'info') {
      return 'View';
    }
    return aboveMdBreakpoint ? 'Update needed' : 'Update';
  };

  const banner = getBanner();
  const button = (
    <StatusButton
      status={valuationStatus}
      onClick={() => setDialogOpen(true)}
      data-test-id="valuation-update-button">
      {getStatusButtonText()}
    </StatusButton>
  );

  const chipLabel =
    status && status === ValuationModel.ValuationStatus.NEW
      ? 'Update Submitted'
      : '';

  return (
    <>
      <DetailCard
        title={name}
        chipLabel={chipLabel}
        action={isUpdateable && Persona.Admin !== persona ? button : undefined}>
        {banner}
        <Typography variant="subtitle1" className={classes.valuationGroupTitle}>
          Valuation Details
        </Typography>
        {status && (
          <DetailCardItem
            label="Last Submitted On"
            value={
              submissionDate
                ? formatDate(submissionDate)
                : formatDate(valuationDate) || Unicode.EmDash
            }
          />
        )}
        {!status && (
          <DetailCardItem
            label="Last Valuation Update"
            value={formatDate(valuationDate) || Unicode.EmDash}
          />
        )}
        <DetailCardItem
          label="Current Value"
          value={formatCurrency(currentValue)}
          isPrivate
        />
        <Typography variant="subtitle1" className={classes.valuationGroupTitle}>
          Asset Details
        </Typography>
        <DetailCardItem label="Asset Type" value={assetType} />
      </DetailCard>
      {
        <ValuationAssetDetail
          accountNumber={accountNumber}
          asset={asset}
          banner={banner}
          setShowDetails={setDialogOpen}
          dialogName={dialogName}
        />
      }
    </>
  );
};

export default ValuationDetailsCard;
